import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CreditCard, Loader2, Trash2, Link2, Copy, Mail } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

// Phase 12-04 (H-06): PCI-safe payment method management.
//
// Raw-card entry has been REMOVED from this modal. Adding a new card is
// delegated to the Fanbasis-hosted checkout flow via the
// `fanbasis-add-payment-method` edge function. The admin receives a URL they
// can copy or email to the client. When the client completes the hosted
// checkout, the `payment.succeeded` webhook handler upserts the real
// `pm_xxx` row into `payment_methods`.

interface PaymentMethod {
  id: string;
  last4: string;
  brand: string | null;
  exp_month: number | null;
  exp_year: number | null;
  is_primary: boolean | null;
  status: string | null;
  type: string;
}

interface EditPaymentMethodModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  clientName: string;
  clientEmail?: string;
  onSuccess?: () => void;
}

export function EditPaymentMethodModal({
  open,
  onOpenChange,
  clientId,
  clientName,
  clientEmail,
  onSuccess,
}: EditPaymentMethodModalProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);

  useEffect(() => {
    if (open && clientId) {
      fetchPaymentMethods();
    } else if (!open) {
      setGeneratedLink(null);
    }
  }, [open, clientId]);

  const fetchPaymentMethods = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("payment_methods")
        .select("*")
        .eq("client_id", clientId)
        .order("is_primary", { ascending: false });

      if (error) throw error;
      setPaymentMethods(data || []);
    } catch (error) {
      console.error("Error fetching payment methods:", error);
      toast.error("Failed to load payment methods");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateLink = async () => {
    setGenerating(true);
    setGeneratedLink(null);
    try {
      const { data, error } = await supabase.functions.invoke(
        "fanbasis-add-payment-method",
        { body: { client_id: clientId } },
      );
      if (error) throw error;
      const url: string | undefined = (data as { payment_link?: string } | null)?.payment_link;
      if (!url) {
        throw new Error("Edge function did not return a payment link");
      }
      setGeneratedLink(url);
      toast.success("Add-payment-method link generated");
    } catch (err) {
      console.error("Error generating add-payment-method link:", err);
      toast.error(
        err instanceof Error ? err.message : "Failed to generate link",
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyLink = async () => {
    if (!generatedLink) return;
    try {
      await navigator.clipboard.writeText(generatedLink);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Clipboard not available — copy the link manually");
    }
  };

  const handleEmailLink = () => {
    if (!generatedLink) return;
    const subject = encodeURIComponent(`Add a payment method — ${clientName}`);
    const body = encodeURIComponent(
      `Hi ${clientName},\n\nPlease use the link below to securely add a payment method on file:\n\n${generatedLink}\n\nThanks,\nVenture Hills`,
    );
    const to = clientEmail ? encodeURIComponent(clientEmail) : "";
    window.open(`mailto:${to}?subject=${subject}&body=${body}`, "_blank");
  };

  const handleSetPrimary = async (methodId: string) => {
    setSaving(true);
    try {
      await supabase
        .from("payment_methods")
        .update({ is_primary: false })
        .eq("client_id", clientId);

      const { error } = await supabase
        .from("payment_methods")
        .update({ is_primary: true })
        .eq("id", methodId);

      if (error) throw error;

      toast.success("Primary payment method updated");
      fetchPaymentMethods();
      onSuccess?.();
    } catch (error) {
      console.error("Error setting primary:", error);
      toast.error("Failed to update primary method");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMethod = async (methodId: string) => {
    if (paymentMethods.length <= 1) {
      toast.error(
        "Cannot delete the last payment method. Clients must have at least 1 on file.",
      );
      return;
    }

    const method = paymentMethods.find((m) => m.id === methodId);
    if (method?.is_primary && paymentMethods.length > 1) {
      toast.error(
        "Cannot delete primary payment method. Set another as primary first.",
      );
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from("payment_methods")
        .delete()
        .eq("id", methodId);

      if (error) throw error;

      toast.success("Payment method removed");
      fetchPaymentMethods();
      onSuccess?.();
    } catch (error) {
      console.error("Error deleting payment method:", error);
      toast.error("Failed to remove payment method");
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status: string | null) => {
    switch (status) {
      case "active":
        return "bg-green-500/20 text-green-400";
      case "expired":
      case "failed":
        return "bg-red-500/20 text-red-400";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Manage Payment Methods</DialogTitle>
          <DialogDescription>
            Update payment methods for {clientName}. Card data is captured via
            Fanbasis's PCI-compliant hosted page — never in this browser.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {paymentMethods.length > 0 ? (
                <div className="space-y-3">
                  {paymentMethods.map((method) => (
                    <div
                      key={method.id}
                      className="flex items-center justify-between p-3 rounded-lg border border-border bg-surface"
                    >
                      <div className="flex items-center gap-3">
                        <CreditCard className="w-5 h-5 text-muted-foreground" />
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">
                              {method.brand || "Card"} •••• {method.last4}
                            </span>
                            {method.is_primary && (
                              <Badge variant="secondary" className="text-xs">
                                Primary
                              </Badge>
                            )}
                          </div>
                          {method.exp_month && method.exp_year ? (
                            <p className="text-sm text-muted-foreground">
                              Expires {method.exp_month}/
                              {method.exp_year?.toString().slice(-2)}
                            </p>
                          ) : null}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={getStatusColor(method.status)}>
                          {method.status || "Unknown"}
                        </Badge>
                        {!method.is_primary && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleSetPrimary(method.id)}
                            disabled={saving}
                          >
                            Set Primary
                          </Button>
                        )}
                        {paymentMethods.length > 1 && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDeleteMethod(method.id)}
                            disabled={saving}
                            title="Delete payment method"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-muted-foreground">
                  <CreditCard className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>No payment methods on file</p>
                </div>
              )}

              {/* Hosted add-payment-method flow (H-06) */}
              <div className="p-4 rounded-lg border border-border bg-muted/30 space-y-3">
                <h4 className="font-medium">Add a new payment method</h4>
                <p className="text-sm text-muted-foreground">
                  Generate a secure Fanbasis-hosted link. Copy or email it to
                  the client; once they complete it, the new card appears here
                  automatically.
                </p>

                <Button
                  onClick={handleGenerateLink}
                  disabled={generating}
                  className="w-full"
                >
                  {generating ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Link2 className="w-4 h-4 mr-2" />
                  )}
                  Generate add-payment-method link
                </Button>

                {generatedLink && (
                  <div className="space-y-2 pt-2">
                    <div className="text-sm break-all p-2 rounded border border-border bg-background">
                      {generatedLink}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleCopyLink}
                        className="flex-1"
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copy link
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleEmailLink}
                        className="flex-1"
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Email to client
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
